import { useLayoutEffect, useRef } from 'react';
import { useCheckoutLocalization } from '../../../hooks/useCheckoutLocalization';
import { translateSelectAtLeast, translateSelectAtMost } from '../../../lib/fieldMessages';

/**
 * The count rules shared by both multi-select layouts (dropdown and list).
 * Required says an answer is needed at all; the minimum says how big an
 * answer must be once given, so an optional field with a minimum accepts
 * nothing or enough. The rule is reported through a mirrored input's custom
 * validity, so the browser's own submit check shows it like any other field.
 */
export function useMultiSelectRules({
  selectedCount,
  required = false,
  min = 0,
  max,
}: {
  selectedCount: number;
  required?: boolean;
  min?: number;
  max?: number;
}) {
  const { locale } = useCheckoutLocalization();
  const requiredInputRef = useRef<HTMLInputElement | null>(null);
  const atMax = typeof max === 'number' && max > 0 && selectedCount >= max;
  const floor = Math.max(min, required ? 1 : 0);
  const needsMore = selectedCount > 0 ? selectedCount < min : Boolean(required);
  const needed = selectedCount > 0 ? min : floor;
  // Only a preloaded selection can sit above the maximum; the rows lock at it.
  const tooMany = typeof max === 'number' && max > 0 && selectedCount > max;

  useLayoutEffect(() => {
    const input = requiredInputRef.current;
    if (!input) return;
    if (tooMany) input.setCustomValidity(translateSelectAtMost(locale, max ?? 0));
    else input.setCustomValidity(needsMore ? translateSelectAtLeast(locale, needed) : '');
  }, [needsMore, needed, tooMany, max, locale]);

  return { requiredInputRef, atMax, needsMore, tooMany };
}
